import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import type {
  BlockedUserRow,
  LedgerRow,
  OnlineUserRow,
  TenantSettings,
  TradingAccountRow,
} from "@/types/api";

export function useOnlineUsers() {
  return useQuery({
    queryKey: ["online-users"],
    queryFn: async () => unwrap<{ rows: OnlineUserRow[]; total: number }>((await api.get("/admin/misc/online-users")).data),
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

export function useBlockedUsers() {
  return useQuery({
    queryKey: ["blocked-users"],
    queryFn: async () => unwrap<{ rows: BlockedUserRow[]; total: number }>((await api.get("/admin/misc/blocked-users")).data),
    staleTime: 30_000,
  });
}

export function useLiveAccounts(type: "live" | "demo" = "live") {
  return useQuery({
    queryKey: ["live-accounts", type],
    queryFn: async () => {
      const p = new URLSearchParams({ type });
      return unwrap<{ rows: TradingAccountRow[]; total: number; totals: { totalBalance: string; totalEquity: string; count: number } }>((await api.get(`/admin/misc/live-accounts?${p.toString()}`)).data);
    },
    staleTime: 30_000,
  });
}

export function useLedger() {
  return useQuery({
    queryKey: ["ledger"],
    queryFn: async () => unwrap<{ rows: LedgerRow[]; total: number; totals: { totalBalance: string; totalHeld: string; totalDeposits: string; totalWithdrawals: string } }>((await api.get("/admin/misc/ledger")).data),
    staleTime: 30_000,
  });
}

export function useTenantSettings() {
  return useQuery({
    queryKey: ["tenant-settings"],
    queryFn: async () => unwrap<TenantSettings>((await api.get("/admin/settings")).data),
    staleTime: 60_000,
  });
}

export function useUpdateTenantSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<Omit<TenantSettings, "id" | "slug" | "planType" | "region">>) => {
      const { data } = await api.patch("/admin/settings", patch);
      return unwrap<TenantSettings>(data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenant-settings"] }),
  });
}

export function useUploadLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/settings/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return unwrap<{ url: string; relativePath: string; size: number; mime: string }>(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-settings"] });
      qc.invalidateQueries({ queryKey: ["current-tenant"] });
    },
  });
}

/** Generic branding-asset upload (favicons, auth backgrounds, banners). */
export interface NotificationRow {
  id: string;
  type: string;
  title: string;
  body: string;
  fullName: string | null;
  email: string | null;
  priority: "critical" | "high" | "normal" | "low";
  createdAt: string;
  link: string;
}

export function useAdminNotifications() {
  return useQuery({
    queryKey: ["admin-notifications"],
    queryFn: async () =>
      unwrap<{ rows: NotificationRow[]; totals: { all: number; byType: Record<string, number> } }>(
        (await api.get("/admin/misc/notifications")).data,
      ),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export interface IbWithdrawRow {
  withdrawId: number;
  userId: number;
  fullName: string;
  email: string;
  amount: string;
  status: string;
  paidAt: string | null;
  createdAt: string;
}
export interface IbFundTransferRow {
  id: number;
  tenantId: number;
  fromIbUserId: number;
  fromIbName?: string | null;
  toTraderUserId: number;
  toTraderName?: string | null;
  amountUsd: string;
  status: string;
  note: string | null;
  createdAt: string;
}
export interface BalanceTransferRow {
  id: number;
  userId: number;
  traderName?: string | null;
  traderEmail?: string | null;
  amountUsd: string;
  tradingAccountId: number | null;
  mt5Login?: number | null;
  direction?: string | null;
  status: string;
  note: string | null;
  createdAt: string;
  completedAt?: string | null;
  senderWallet?: number;
  recipientName?: string | null;
  recipientEmail?: string | null;
  recipientWallet?: number | null;
  fromRole?: string | null;
  toRole?: string | null;
  typeLabel?: string | null;
}
export interface IbBalanceAddRow {
  id: number;
  fromIbUserId: number;
  toTraderUserId: number;
  amountUsd: string;
  status: string;
  note: string | null;
  createdAt: string;
}
export interface IndividualLedger {
  user: { id: number; fullName: string; email: string };
  wallets: Array<{ id: number; userId: number; currency: string; balance: string; heldBalance: string; totalDepositedUsd: string; totalWithdrawnUsd: string }>;
}

export function useIbWithdrawReport() {
  return useQuery({
    queryKey: ["report-ib-withdraw"],
    queryFn: async () => unwrap<{ rows: IbWithdrawRow[]; total: number }>((await api.get("/admin/misc/ib-withdraw")).data),
    staleTime: 30_000,
  });
}
export function useIbFundTransferReport() {
  return useQuery({
    queryKey: ["report-ib-fund-transfer"],
    queryFn: async () => unwrap<{ rows: IbFundTransferRow[]; total: number }>((await api.get("/admin/misc/ib-fund-transfer")).data),
    staleTime: 30_000,
  });
}
export function useExternalFundTransferReport() {
  return useQuery({
    queryKey: ["report-external-fund-transfer"],
    queryFn: async () => unwrap<{ rows: BalanceTransferRow[]; total: number }>((await api.get("/admin/misc/external-fund-transfer")).data),
    staleTime: 30_000,
  });
}
export function useIbBalanceAddReport() {
  return useQuery({
    queryKey: ["report-ib-balance-add"],
    queryFn: async () => unwrap<{ rows: IbBalanceAddRow[] }>((await api.get("/admin/misc/ib-balance-add")).data),
    staleTime: 30_000,
  });
}
export function useIndividualLedger(userId: number | null) {
  return useQuery({
    queryKey: ["individual-ledger", userId],
    queryFn: async () => unwrap<IndividualLedger>((await api.get(`/admin/misc/individual-ledger/${userId}`)).data),
    enabled: !!userId,
  });
}

export function useUploadBrandingAsset() {
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/settings/branding-asset", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return unwrap<{ url: string; relativePath: string; size: number; mime: string }>(data);
    },
  });
}
